/* istanbul ignore file */

import { type PropsWithChildren, Suspense, useEffect, useState } from "react";
import { configureConstraint, loadConstraints } from "@42.nl/jarb-final-form";
import { configureEnums, loadEnums } from "@42.nl/react-spring-enums";
import { useQueries } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"; // By default, only included in development bundles
import { ThemeWrapper } from "@timeedit/ui-components";
import { App as AntApp } from "antd";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import GlobalLoaderProvider from "@components/wrappers/globalLoader.wrapper";
import { combineQuery } from "@services/tanstack.service";
import { ErrorBoundaryDefault } from "@src/app/components/ErrorBoundary/ErrorBoundaryDefault.component";
import TanstackQueryProvider from "@src/providers/tanstackQuery.provider";
import YearProvider from "@src/providers/year.provider";
import App from "./app/App";
import { CustomerStyle } from "./app/components/CustomerStyle/CustomerStyle";
import FlashMessages from "./app/components/FlashMessages/FlashMessages";
import useFilter from "./app/filters/filter.hook";
import { TabsProvider } from "./app/tabs/context/TabsContext";
import handleErrors from "./core/error";
import { unregister } from "./core/registerServiceWorker";
import "./styles/app/app.scss";
import AuthProvider from "./providers/auth.provider";
import I18NextProvider from "./providers/i18n.provider";
import ParameterProvider from "./providers/parameter.provider";

// Import to make Webpack include favicons for index.html
require.context("./images/favicons", true, /\.(ico|png|svg|webmanifest)$/);

declare global {
  interface Window {
    reactFetchProgressBarInitialized?: boolean;
  }
}

configureConstraint({
  constraintsUrl: "/api/constraints"
});

configureEnums({
  enumsUrl: "/api/enums"
});

handleErrors();
loadConstraints();
loadEnums();

const rootElement = document.getElementById("root");

if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<Main />);
}

function Main() {
  const [showDevtools, setShowDevtools] = useState(
    process.env.NODE_ENV === "development"
  );

  useEffect(() => {
    // @ts-expect-error does not exist on window
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  return (
    <ErrorBoundaryDefault>
      <GlobalLoaderProvider>
        <ThemeWrapper defaultComponentSize="middle">
          <AntApp className="sg-h-full">
            <Suspense>
              <TanstackQueryProvider>
                <ParameterProvider>
                  <I18NextProvider>
                    <BrowserRouter>
                      <AuthProvider>
                        <YearProvider>
                          <BootApp>
                            <TabsProvider>
                              <FlashMessages />
                              <App />
                              {showDevtools && (
                                <Suspense>
                                  <ReactQueryDevtools initialIsOpen={false} />
                                </Suspense>
                              )}
                            </TabsProvider>
                          </BootApp>
                        </YearProvider>
                      </AuthProvider>
                    </BrowserRouter>
                  </I18NextProvider>
                </ParameterProvider>
              </TanstackQueryProvider>
            </Suspense>
          </AntApp>
        </ThemeWrapper>
      </GlobalLoaderProvider>
    </ErrorBoundaryDefault>
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
unregister();

function BootApp({ children }: Readonly<PropsWithChildren>) {
  const { filters } = useFilter();

  const { data: fetchedData } = useQueries({
    queries: [
      { queryKey: ["constraints"], queryFn: () => loadConstraints },
      { queryKey: ["enums"], queryFn: () => loadEnums }
    ],
    combine: combineQuery
  });

  const { t } = useTranslation("translation");
  const title = t("BROWSER_TITLE");

  useEffect(() => {
    document.title = title;
  }, [title]);

  if (!fetchedData || !filters) {
    return <></>;
  }

  return <CustomerStyle>{children}</CustomerStyle>;
}
